// Phase H.13a — Version history drawer for the template editor.
//
// Slide-in panel on the right of the editor. Three modes:
//   1. List — newest-first list of version rows.
//   2. Preview — read-only render of a chosen version's body_html.
//   3. Diff — word-level diff against the most-recent persisted body_html.
//
// Restore is super_admin only; for regular admins the button is rendered
// disabled with a tooltip. We don't gate this on the client — the backend
// returns 403 if a regular admin tries — but disabling avoids a confusing
// red toast on click.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { diffWords } from "diff";
import { toast } from "sonner";

import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Loader2, History, ChevronLeft, Eye, GitCompare, RotateCcw, RefreshCw, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

function _humanTimeAgo(iso) {
    if (!iso) return "—";
    try {
        const t = Date.parse(iso);
        if (Number.isNaN(t)) return iso;
        const secs = Math.max(0, Math.round((Date.now() - t) / 1000));
        if (secs < 60) return `${secs}s ago`;
        const mins = Math.round(secs / 60);
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.round(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.round(hrs / 24);
        if (days < 30) return `${days}d ago`;
        const d = new Date(t);
        return d.toLocaleDateString();
    } catch (_) {
        return iso;
    }
}

function _triggerLabel(v) {
    if (v.is_restore_of) return `restored from v${v.restored_from_number || "?"}`;
    return v.trigger === "auto" ? "auto-save" : "manual save";
}

// Word-level diff renderer. Insertions = green pill, deletions = red strike.
// We diff the RAW HTML string (per the H.13a brief) rather than parsing
// the doc structure — simpler, and matches the user's mental model of
// "what changed in the text".
function DiffView({ before, after }) {
    const parts = useMemo(() => diffWords(before || "", after || ""), [before, after]);
    return (
        <div className="font-mono text-xs leading-relaxed whitespace-pre-wrap break-words p-3 bg-white border border-slate-200 rounded-md max-h-[60vh] overflow-auto"
            data-testid="version-diff-view">
            {parts.map((p, i) => {
                if (p.added) {
                    return (
                        <span key={i} className="bg-green-100 text-green-900 px-0.5 rounded"
                            data-testid="diff-added">{p.value}</span>
                    );
                }
                if (p.removed) {
                    return (
                        <span key={i} className="bg-red-100 text-red-900 line-through px-0.5 rounded"
                            data-testid="diff-removed">{p.value}</span>
                    );
                }
                return <span key={i} className="text-slate-500">{p.value}</span>;
            })}
        </div>
    );
}

export default function VersionHistoryDrawer({
    open,
    onClose,
    templateId,
    canRestore = false,           // true when user is super_admin
    currentBodyHtml = "",         // most-recent PERSISTED body_html (not the unsaved editor draft)
    onRestored,                   // callback(updatedTemplate) → parent reloads editor state
}) {
    const [items, setItems] = useState([]);
    const [maxVersions, setMaxVersions] = useState(100);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);  // version metadata
    const [fullVersion, setFullVersion] = useState(null);  // body_html etc.
    const [view, setView] = useState("preview");   // 'preview' | 'diff'
    const [restoring, setRestoring] = useState(false);
    const [confirmRestore, setConfirmRestore] = useState(false);

    const refresh = useCallback(async () => {
        if (!templateId) return;
        setLoading(true);
        try {
            const r = await api.get(`/admin/templates/${templateId}/versions?limit=100`);
            setItems(r.data?.items || []);
            setMaxVersions(r.data?.max_versions || 100);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [templateId]);

    useEffect(() => {
        if (open) refresh();
    }, [open, refresh]);

    const handleSelect = async (v) => {
        setSelected(v);
        setFullVersion(null);
        setView("preview");
        try {
            const r = await api.get(`/admin/templates/${templateId}/versions/${v.id}`);
            setFullVersion(r.data);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const handleRestore = async () => {
        if (!selected) return;
        setRestoring(true);
        try {
            const r = await api.post(`/admin/templates/${templateId}/versions/${selected.id}/restore`);
            toast.success(`Restored from Version ${selected.version_number} — new version created`);
            if (onRestored) onRestored(r.data);
            setConfirmRestore(false);
            await refresh();
            // Auto-select the newest version so the user can see what landed.
            // (it's the restore record itself)
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRestoring(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex" data-testid="version-history-drawer">
            {/* Backdrop. Click anywhere outside to close. */}
            <button
                type="button"
                aria-label="Close version history"
                onClick={onClose}
                className="flex-1 bg-slate-900/30 backdrop-blur-[1px]"
            />
            <aside className="w-[640px] max-w-full bg-slate-50 border-l border-slate-200 shadow-2xl flex flex-col">
                <header className="px-4 py-3 border-b border-slate-200 bg-white flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                            <History size={18} /> Version history
                        </h2>
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <p data-testid="version-history-cap-caption"
                                        className="text-[11px] text-slate-500 underline decoration-dotted cursor-help mt-0.5">
                                        Up to {maxVersions} versions kept
                                    </p>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-[220px] text-xs">
                                    Older versions are automatically removed to manage storage.
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                    <div className="flex items-center gap-1">
                        <button type="button" onClick={refresh}
                            data-testid="version-history-refresh"
                            title="Refresh"
                            className="h-8 px-2 rounded-md hover:bg-slate-100 text-slate-600 inline-flex items-center justify-center">
                            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                        </button>
                        <button type="button" onClick={onClose}
                            data-testid="version-history-close"
                            title="Close"
                            className="h-8 px-2 rounded-md hover:bg-slate-100 text-slate-600 inline-flex items-center justify-center">
                            <X size={14} />
                        </button>
                    </div>
                </header>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-[280px_1fr] overflow-hidden">
                    {/* Versions list */}
                    <ul className="overflow-y-auto border-r border-slate-200 bg-white"
                        data-testid="version-history-list">
                        {loading && items.length === 0 && (
                            <li className="p-4 text-sm text-slate-500 flex items-center gap-2">
                                <Loader2 className="animate-spin" size={14} /> Loading versions…
                            </li>
                        )}
                        {!loading && items.length === 0 && (
                            <li className="p-4 text-sm text-slate-500" data-testid="version-history-empty">
                                No versions yet — save the template to create the first one.
                            </li>
                        )}
                        {items.map((v) => {
                            const isSelected = selected?.id === v.id;
                            return (
                                <li key={v.id}>
                                    <button
                                        type="button"
                                        data-testid={`version-row-${v.version_number}`}
                                        onClick={() => handleSelect(v)}
                                        className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${isSelected ? "bg-blue-50 border-l-2 border-l-blue-700" : ""}`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-sm font-medium text-slate-900">
                                                Version {v.version_number}
                                            </span>
                                            <span className="text-[10px] uppercase tracking-wider text-slate-500">
                                                {_triggerLabel(v)}
                                            </span>
                                        </div>
                                        <div className="text-[11px] text-slate-500 mt-0.5">
                                            {_humanTimeAgo(v.saved_at)} · {v.saved_by_email || "—"}
                                        </div>
                                        {v.note && (
                                            <div className="text-[11px] text-slate-600 italic mt-0.5">
                                                {v.note}
                                            </div>
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>

                    {/* Preview / diff pane */}
                    <section className="overflow-y-auto p-4">
                        {!selected && (
                            <div className="h-full flex items-center justify-center text-sm text-slate-400"
                                data-testid="version-detail-empty">
                                Select a version on the left to preview or compare with current content.
                            </div>
                        )}
                        {selected && (
                            <div>
                                <div className="flex items-center justify-between gap-2 mb-3">
                                    <div>
                                        <h3 className="text-base font-semibold text-slate-900">
                                            Version {selected.version_number}
                                        </h3>
                                        <p className="text-xs text-slate-500">
                                            {_humanTimeAgo(selected.saved_at)} · {selected.saved_by_email || "—"} · {_triggerLabel(selected)}
                                        </p>
                                    </div>
                                    <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-white" role="tablist">
                                        <button type="button" onClick={() => setView("preview")}
                                            data-testid="version-view-preview"
                                            className={`px-2 h-7 text-xs font-medium rounded inline-flex items-center gap-1 ${view === "preview" ? "bg-blue-900 text-white" : "text-slate-600"}`}>
                                            <Eye size={12} /> Preview
                                        </button>
                                        <button type="button" onClick={() => setView("diff")}
                                            data-testid="version-view-diff"
                                            className={`px-2 h-7 text-xs font-medium rounded inline-flex items-center gap-1 ${view === "diff" ? "bg-blue-900 text-white" : "text-slate-600"}`}>
                                            <GitCompare size={12} /> Compare with current
                                        </button>
                                    </div>
                                </div>
                                {!fullVersion && (
                                    <div className="h-32 flex items-center justify-center text-sm text-slate-400">
                                        <Loader2 size={14} className="animate-spin mr-2" /> Loading version…
                                    </div>
                                )}
                                {fullVersion && view === "preview" && (
                                    <div
                                        data-testid="version-preview-body"
                                        className="prose prose-sm max-w-none p-3 bg-white border border-slate-200 rounded-md max-h-[60vh] overflow-auto"
                                        dangerouslySetInnerHTML={{ __html: fullVersion.body_html || "<p class='text-slate-400'>Empty body</p>" }}
                                    />
                                )}
                                {fullVersion && view === "diff" && (
                                    <DiffView before={fullVersion.body_html || ""} after={currentBodyHtml || ""} />
                                )}

                                {/* Footer actions */}
                                <div className="mt-3 flex items-center justify-end gap-2">
                                    <TooltipProvider delayDuration={250}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span tabIndex={canRestore ? -1 : 0}>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="default"
                                                        disabled={!canRestore || restoring || !fullVersion}
                                                        onClick={() => setConfirmRestore(true)}
                                                        data-testid="version-restore-button"
                                                        className="bg-blue-900 hover:bg-blue-800"
                                                    >
                                                        {restoring ? <Loader2 size={12} className="animate-spin mr-1" /> : <RotateCcw size={12} className="mr-1" />}
                                                        Restore this version
                                                    </Button>
                                                </span>
                                            </TooltipTrigger>
                                            {!canRestore && (
                                                <TooltipContent side="left" className="text-xs max-w-[240px]">
                                                    Only Super Admins can restore previous versions.
                                                </TooltipContent>
                                            )}
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            </div>
                        )}
                    </section>
                </div>

                {/* Confirm-restore modal (kept inside the drawer for layering). */}
                {confirmRestore && selected && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-900/40 p-4"
                        data-testid="restore-confirm-modal">
                        <div className="bg-white rounded-lg shadow-xl max-w-[420px] w-full p-5">
                            <h4 className="text-base font-semibold text-slate-900 mb-2 inline-flex items-center gap-2">
                                <RotateCcw size={16} /> Restore Version {selected.version_number}?
                            </h4>
                            <p className="text-sm text-slate-600 mb-4">
                                This will replace your current content with Version {selected.version_number} from
                                {" "}{_humanTimeAgo(selected.saved_at)}. Your current content will be saved as a new version first.
                            </p>
                            <div className="flex items-center justify-end gap-2">
                                <Button type="button" variant="ghost" size="sm"
                                    data-testid="restore-confirm-cancel"
                                    onClick={() => setConfirmRestore(false)}
                                    disabled={restoring}>
                                    Cancel
                                </Button>
                                <Button type="button" size="sm"
                                    data-testid="restore-confirm-go"
                                    className="bg-blue-900 hover:bg-blue-800"
                                    onClick={handleRestore}
                                    disabled={restoring}>
                                    {restoring ? <Loader2 size={12} className="animate-spin mr-1" /> : <ChevronLeft size={12} className="mr-1" />}
                                    Yes, restore
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </aside>
        </div>
    );
}
